import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useLocale } from '../contexts/LocaleContext'
import TransitionBanner from './TransitionBanner'
import Header from './Header'
import Footer from './Footer'

interface LegalPageProps {
  /** Key under `legal` in the locale files, e.g. `privacyPolicy`. */
  document: string
  /** Section keys in the order they should be read. */
  sections: (string | { key: string; subsections: string[] })[]
}

interface SectionShape {
  title?: string
  content?: string
  list?: string[]
}

/**
 * The three legal documents differ only in their sections, so they share a renderer.
 * Each section is `{ title, content, list }` in the locale file, which keeps the copy
 * translatable without a component per clause.
 */
const LegalPage: React.FC<LegalPageProps> = ({ document, sections }) => {
  const { t } = useLocale()
  const read = (path: string) => t(`legal.${document}.${path}`) as unknown as SectionShape

  const renderBody = (section: SectionShape) => (
    <>
      {section.content && (
        <p className="mt-2 leading-relaxed text-text-secondary">{section.content}</p>
      )}
      {Array.isArray(section.list) && (
        <ul className="mt-3 list-disc space-y-1.5 pl-5 text-text-secondary">
          {section.list.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </>
  )

  return (
    <div className="min-h-screen">
      <TransitionBanner />
      <Header />

      <main className="mx-auto max-w-[760px] px-5 py-12 sm:px-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-md font-semibold text-action-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.back')}
        </Link>

        <h1 className="mt-6 font-display text-[32px] font-bold tracking-display sm:text-[38px]">
          {t(`legal.${document}.title`)}
        </h1>
        <p className="mt-3 text-[17px] leading-relaxed text-text-secondary">
          {t(`legal.${document}.subtitle`)}
        </p>
        <p className="mt-2 text-sm text-text-muted">{t(`legal.${document}.lastUpdated`)}</p>

        <div className="mt-8 rounded-lg border border-border bg-surface-card p-6 shadow-card sm:p-8">
          {sections.map((entry) => {
            const key = typeof entry === 'string' ? entry : entry.key
            const section = read(key)

            return (
              <section key={key} className="mb-8 last:mb-0">
                <h2 className="font-display text-2xl font-bold">{section.title}</h2>
                {renderBody(section)}

                {typeof entry !== 'string' &&
                  entry.subsections.map((child) => {
                    const sub = read(`${key}.${child}`)
                    return (
                      <div key={child} className="mt-5">
                        <h3 className="text-lg font-semibold text-text-primary">{sub.title}</h3>
                        {renderBody(sub)}
                      </div>
                    )
                  })}
              </section>
            )
          })}
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default LegalPage
